import React from 'react';

import { contactApis } from '@apis/next/contactApis';
import { Button, Button as CommonButton } from '@components/Button';
import { GridForm } from '@components/NextForms/GridForm';
import { QueryContainer } from '@components/QueryContainer';
import { SortButtons } from '@components/sort/SortButtons';
import { ContactGrid } from '@components/utilities/Contact/ContactGrid';
import { VerticalSpace } from '@components/VerticalSpace';
import { bookingInquiryStrings } from '@data/bookingInquiry';
import { useSearch } from '@hooks/next/useSearch';
import { useCompany } from '@hooks/queries/useCompany';
import { useUser } from '@hooks/queries/useUser';
import { CompanyRole, rolesCanAddConsignee, rolesCanAddShipper } from '@models/Company';
import { ContactType, ContactSearchQuery } from '@models/next/Contact';
import { empty } from '@monoid-dev/mobx-zod-form';
import { useForm } from '@monoid-dev/mobx-zod-form-react';
import { makeMuiStyles, useCommonStyles, useIsMobile } from '@styles';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import clsx from 'clsx';
import { useHistory } from 'react-router-dom';

const strings = createStrings({
  ja: {
    search: '検索',
    reset: 'リセット',
    addAddress: '新規登録',
    sortByUseCountDesc: '利用回数順',
    sortByCreatedAtAsc: '登録順',
    companyName: '会社名',
    principalName: '担当者名',
    addD2DMember: '+D2D MEMBERS',
    noData: (type: ContactType, onClick?: () => void) => (
      <>
        まだ{bookingInquiryStrings[type]}を登録していません。
        <br />
        <span>
          <a onClick={onClick}>「新規登録」</a>
          をクリックして{bookingInquiryStrings[type]}を登録してください。
        </span>
      </>
    ),
    noSearchResult: () => (
      <>
        検索条件に当てはまるデータが見つかりませんでした。
        <br />
        検索条件を変えて検索してみてください。
      </>
    ),
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    ...common.flexColumn,
    flex: 1,
  },
  toolbar: {
    ...common.flexInline,
    ...common.justifyBetween,
    [theme.breakpoints.down('xs')]: {
      ...common.flexColumn,
      gap: 10,
    },
  },
  flexSpace: {
    flex: 1,
  },
  toolbarLeadingButtons: {
    ...common.flexInline,
    gap: 22,
  },
  toolbarTrailingButtons: {
    ...common.flexInline,
    gap: 22,
  },
  buttons: {
    ...common.flexInline,
    columnGap: theme.spacing(3),
  },
  button: {
    width: '133px',
  },
}));

const ContactSearch: React.FC = () => {
  const { search, updateSearch } = useSearch(ContactSearchQuery);

  const isMobile = useIsMobile();

  const form = useForm(ContactSearchQuery, {
    initialOutput: search,
    enableReinitialize: true,
  });

  const { fields } = form.root;

  return (
    <form {...form.bindForm({ onSubmit: updateSearch })}>
      <GridForm
        columns={isMobile ? 1 : 4}
        fields={[fields.company_name, fields.principal_name]}
        buttons={
          <>
            <Button
              color="primary"
              size="small"
              type="submit"
              text={formStrings.search}
              style={{ width: 88 }}
            />
            <Button
              color="primary"
              size="small"
              type="submit"
              text={formStrings.reset}
              onClick={() => form.root.setOutput(empty)}
              style={{ width: 88 }}
            />
          </>
        }
      />
    </form>
  );
};

export interface ContactBookProps {
  largeTitle?: string;
  contactType: ContactType;
  addAddressUrl?: string;
}

export const ContactBook: React.FC<ContactBookProps> = (props) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { largeTitle, contactType, addAddressUrl } = props;

  const company = useCompany();

  const { search, updateSearch } = useSearch(ContactSearchQuery);

  const queries = {
    ...search,
    ordering: search.ordering ?? ['-use_count'],
    contact_type: contactType,
  } satisfies ContactSearchQuery;

  const isAbleToAddAddress =
    (contactType === 'shipper' && rolesCanAddShipper.includes(company.data?.role as CompanyRole)) ||
    (contactType === 'consignee' &&
      rolesCanAddConsignee.includes(company.data?.role as CompanyRole));

  const contactQuery = contactApis.useGet('/contact', {
    queries,
  });

  const user = useUser();

  const history = useHistory();

  const onAddAddress = () => {
    if (addAddressUrl) {
      history.push(addAddressUrl!);
    }
  };

  const isSearching = !!(search.company_name || search.principal_name);

  return (
    <div className={clsx(classes.container)}>
      <VerticalSpace height={16} />
      <div className={clsx(classes.pageTitleText, classes.textPrimary)}>{largeTitle ?? ''}</div>
      <VerticalSpace height={16} />
      <ContactSearch />
      <VerticalSpace height={16} />
      <div className={classes.toolbar}>
        <div className={classes.toolbarLeadingButtons}>
          {isAbleToAddAddress && (
            <CommonButton
              text={strings.addAddress}
              color="primary"
              size="small"
              className={classes.button}
              onClick={onAddAddress}
            />
          )}
        </div>

        <div className={classes.flexSpace} />
        <div className={classes.toolbarTrailingButtons}>
          <div className={classes.flexSpace} />
          <SortButtons
            currentSortKey={queries.ordering[0]}
            items={[
              {
                title: strings.sortByUseCountDesc,
                sortKey: '-use_count',
              },
              {
                title: strings.sortByCreatedAtAsc,
                sortKey: '-created_at',
              },
            ]}
            onSortKeyChanged={(ordering) => updateSearch({ ordering: [ordering] })}
          />
        </div>
      </div>
      <VerticalSpace height={30} />
      <QueryContainer queries={[contactQuery, user, company]}>
        <ContactGrid
          contactType={contactType}
          noDataTip={
            isSearching ? strings.noSearchResult() : strings.noData(contactType, onAddAddress)
          }
          contacts={contactQuery.data!}
          user={user.data!}
          company={company.data!}
        />
      </QueryContainer>

      <VerticalSpace height={30} />
    </div>
  );
};
